import { useEffect, useState } from 'react';
import {
    getSalesPersonsCommonSalesPersonsGet,
    getWorkTypesCommonWorkTypesGet,
    getManufacturersCommonManufacturersGet
} from '../client/services.gen';
import { SalesPersonResponse, WorkTypeResponse, ManufacturerResponse } from '../client/types.gen';

// 営業担当者一覧を取得するカスタムフック
export const useSalesPersons = () => {
    const [salesPersons, setSalesPersons] = useState<SalesPersonResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchSalesPersons = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getSalesPersonsCommonSalesPersonsGet();
            setSalesPersons(response);
        } catch (err) {
            setError('営業担当者の取得に失敗しました');
            console.error('Failed to fetch sales persons:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesPersons();
    }, []);

    return {
        salesPersons,
        loading,
        error,
        refetch: fetchSalesPersons
    };
};

// 作業名称一覧を取得するカスタムフック
export const useWorkTypes = () => {
    const [workTypes, setWorkTypes] = useState<WorkTypeResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchWorkTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getWorkTypesCommonWorkTypesGet();
            setWorkTypes(response);
        } catch (err) {
            setError('作業名称の取得に失敗しました');
            console.error('Failed to fetch work types:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkTypes();
    }, []);

    return {
        workTypes,
        loading,
        error,
        refetch: fetchWorkTypes
    };
};

// 製造メーカー一覧を取得するカスタムフック
export const useManufacturers = () => {
    const [manufacturers, setManufacturers] = useState<ManufacturerResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchManufacturers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getManufacturersCommonManufacturersGet();
            setManufacturers(response);
        } catch (err) {
            setError('製造メーカーの取得に失敗しました');
            console.error('Failed to fetch manufacturers:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchManufacturers();
    }, []);

    return {
        manufacturers,
        loading,
        error,
        refetch: fetchManufacturers
    };
};

// 営業担当者の選択肢を文字列配列として取得するフック（既存コードとの互換性のため）
export const useSalesPersonOptions = () => {
    const { salesPersons, loading, error } = useSalesPersons();

    const options = salesPersons.map(person => person.name);

    return { options, loading, error };
};

// 営業担当者の選択肢をIDと名前を含むオブジェクト配列として取得するフック
export const useSalesPersonOptionsWithId = () => {
    const { salesPersons, loading, error } = useSalesPersons();

    const optionsWithId = salesPersons.map(person => ({
        id: person.id,
        name: person.name,
        label: person.name  // Autocompleteで使用
    }));

    return { optionsWithId, loading, error };
};

// 作業名称の選択肢を文字列配列として取得するフック（既存コードとの互換性のため）
export const useWorkTypeOptions = () => {
    const { workTypes, loading, error } = useWorkTypes();

    const options = workTypes.map(type => type.name);

    return { options, loading, error };
};

// 作業名称の選択肢をIDと名前を含むオブジェクト配列として取得するフック
export const useWorkTypeOptionsWithId = () => {
    const { workTypes, loading, error } = useWorkTypes();

    const optionsWithId = workTypes.map(type => ({
        id: type.id,
        name: type.name,
        label: type.name  // Autocompleteで使用
    }));

    return { optionsWithId, loading, error };
};

// 製造メーカーの選択肢を文字列配列として取得するフック（既存コードとの互換性のため）
export const useManufacturerOptions = () => {
    const { manufacturers, loading, error } = useManufacturers();

    const options = manufacturers.map(manufacturer => manufacturer.name);

    return { options, loading, error };
};

// 製造メーカーの選択肢をIDと名前を含むオブジェクト配列として取得するフック
export const useManufacturerOptionsWithId = () => {
    const { manufacturers, loading, error } = useManufacturers();

    const optionsWithId = manufacturers.map(manufacturer => ({
        id: manufacturer.id,
        name: manufacturer.name,
        label: manufacturer.name  // Autocompleteで使用
    }));

    return { optionsWithId, loading, error };
};